import { camelize } from "camelscore";

export type TwoFactorChallenge = {
  challengeToken: string;
  kind: "email";
  sentTo: string;
  expiresIn: number;
  resendAvailableIn: number;
  attemptsRemaining: number;
};

export type NextAction =
  | { type: "sign_in" }
  | { type: "two_factor_challenge"; twoFactorChallenge: TwoFactorChallenge };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isTwoFactorChallenge = (value: unknown): value is TwoFactorChallenge =>
  isRecord(value) &&
  typeof value.challengeToken === "string" &&
  value.kind === "email" &&
  typeof value.sentTo === "string" &&
  typeof value.expiresIn === "number" &&
  typeof value.resendAvailableIn === "number" &&
  typeof value.attemptsRemaining === "number";

/**
 * Narrows an already camelized `next_action` from a successful response.
 */
export const parseNextAction = (value: unknown): NextAction | undefined => {
  if (!isRecord(value)) return undefined;

  if (value.type === "sign_in") return { type: "sign_in" };

  if (
    value.type === "two_factor_challenge" &&
    isTwoFactorChallenge(value.twoFactorChallenge)
  ) {
    return {
      type: "two_factor_challenge",
      twoFactorChallenge: value.twoFactorChallenge,
    };
  }

  return undefined;
};

/**
 * Error response bodies are not camelized by the request helpers, so the
 * `next_action` sitting next to `error` arrives in snake case.
 */
export const nextActionFromErrorBody = (
  body: unknown,
): NextAction | undefined => {
  if (!isRecord(body)) return undefined;

  const camelizedNextAction: unknown = camelize(body.next_action);
  return parseNextAction(camelizedNextAction);
};
